/* ============================================
 * Free Gifts (BOGOs) – UI custom do modal
 * Requisitos atendidos:
 * - Respeita evento/abertura padrão do BOGOs (elegibilidade).
 * - Escolha de 1 brinde OU declínio (apenas no modal de confirmação).
 * - Declínio registra opt-out (analytics) e bloqueia reabertura automática.
 * - Reabertura só via CTA no carrinho (banner) chamando openGiftsPopup(true).
 * - Ao voltar a ficar elegível com uma oferta DIFERENTE, o modal pode abrir novamente.
 * - Adição ao carrinho sem reload (via addGiftToCartFunc do app).
 * - Troca a imagem ao escolher variante.
 * - Slider com Glide com breakpoints.
 * ============================================ */

/* ---------- util simples para storage local ---------- */
const FGC_STORAGE = {
  get(key) { try { return localStorage.getItem(key); } catch (_) { return null; } },
  set(key, val) { try { localStorage.setItem(key, String(val)); } catch (_) {} },
  del(key) { try { localStorage.removeItem(key); } catch (_) {} }
};

/* chaves usadas no localStorage */
const FGC_KEYS = {
  OPTED_OUT: 'fg_no_gift',          // "1" se usuário declinou
  CHOSEN: 'fg_gift_chosen',         // "1" se usuário escolheu um brinde
  LAST_OFFER: 'fg_last_offer_key',  // assinatura da última oferta vista (para permitir reabrir quando a oferta mudar)
  LAST_ELIGIBLE: 'fg_last_eligible',
  HAS_OFFERS: 'fg_has_offers'
};

/* ---------- Slider minimalista baseado em Glide ---------- */
class FgcGlide extends HTMLElement {
  // Helper: calcula automaticamente perView baseado no tamanho do container e largura dos cards
  _autoPerView(gap){
    try {
      const track = this.querySelector('.glide__track');
      const wrapW = (track ? track.clientWidth : this.clientWidth) || 0;
      const firstCard = this.querySelector('.fgc-card') || this.querySelector('.glide__slide');
      let cardW = firstCard ? firstCard.getBoundingClientRect().width : 220;
      if (!cardW || cardW <= 0) cardW = 220;
      const pv = Math.max(1, Math.floor((wrapW + gap) / (cardW + gap)));
      return Math.min(pv, 8); // limite superior razoável
    } catch(_) { return 1; }
  }

  // Helper: liga ResizeObserver para recalcular perView automaticamente em resize
  _bindResizeAuto(gap){
    if (this._ro) return;
    const track = this.querySelector('.glide__track') || this;
    try {
      this._ro = new ResizeObserver(() => {
        if (!this.glide) return;
        const pv = this._autoPerView(gap);
        try { this.glide.update({ perView: pv }); } catch(_) {}
      });
      this._ro.observe(track);
    } catch(_) {}
  }
  connectedCallback() {
    this._mountWhenSlidesExist();
  }
  disconnectedCallback() {
    if (this._mo) { try { this._mo.disconnect(); } catch(_){} this._mo = null; }
    if (this.glide) { try { this.glide.destroy(); } catch(_){} this.glide = null; }
    if (this._ro) { try { this._ro.disconnect(); } catch(_){} this._ro = null; }
    this._initScheduled = false;
  }
  _mountWhenSlidesExist() {
    if (this._mo) return;
    const ul = this.querySelector('.glide__slides');
    this._tryInit();
    if (!ul) return;
    this._mo = new MutationObserver(() => this._scheduleTryInit());
    this._mo.observe(ul, { childList: true });
  }
  _scheduleTryInit() {
    // Debounce para coalescer mutações rápidas em um update por frame
    if (this._initScheduled) return;
    this._initScheduled = true;
    requestAnimationFrame(() => {
      this._initScheduled = false;
      this._tryInit();
    });
  }
  _tryInit() {
    const ul = this.querySelector('.glide__slides');
    const hasSlides = !!(ul && ul.children && ul.children.length);
    const count = ul ? ul.children.length : 0;
    if (!hasSlides) return;
    if (this.glide) {
      try { this.glide.update(); } catch(_) {}
      return;
    }
    if (!window.Glide) { console.warn('[FG] Glide não disponível.'); return; }

    console.info('[FG] Inicializando slider Glide...', ul.children.length);

    if (count === 2) {
      // this.classList.add('fgc-glide--no-arrows');
      this.glide = new Glide(this, {
        type: 'slider',
        perView: count,
        gap: 16,
        keyboard: true,
        bound: true,
        rewind: false,
        perTouch: 1,
        peek: { before: 24, after: 24 },
        perMove: 1, 
        animationDuration: 260,
        breakpoints: {
          414:  { gap: 8 },
          360:  { perView: 1, gap: 8, peek: { before: 16, after: 16 } }
        }
      });
      this.glide.mount();
    }  else if (count > 2) {
      this.glide = new Glide(this, {
        type: 'slider',
        perView: 3,
        gap: 16,
        keyboard: true,
        bound: true,
        rewind: false,
        perTouch: 1,
        perMove: 1, 
        peek: { before: 24, after: 24 },
        animationDuration: 260,
        breakpoints: {
          600:  { perView: 2, gap: 12 },
          414:  { perView: 2, gap: 8 },
          360:  { perView: 1, gap: 8, peek: { before: 16, after: 16 }  }
        }
      });
      this.glide.mount();
    }
  }
}

class FreeGiftsModal extends HTMLElement {
  constructor() {
    super();
    // refs do DOM (cacheados no connected)
    this.$modal   = null;
    this.$items   = null;
    this.$confirm = null;

    // estado mínimo
    this.payload         = null;            // último payload do BOGOs (contém addGiftToCartFunc)
    this.selectedVariant = null;            // { id, offerId, cardEl }
    this.lastFocused     = null;            // para retornar foco ao fechar

    // bridge do banner (controlado pelo modal)
    this.$banner = null;
    this._cartEvents = [
      'cart:updated','cart-updated','cart:change','cart:changed'
    ];
    this._bannerMO = null; // MutationObserver para rebind automático do banner

    // controle de refresh do drawer
    this._refreshingCart = false;
    this._pendingCartRefresh = false;

    // controle de detecção de ofertas sem abrir o modal
    this._suppressAutoOpenOnce = false;
    this._probingOffers = false;

    // bloqueio visual por login. A elegibilidade e o payload continuam vindo do BOGOS.
    this.requiresLogin = false;
    this.customerLoggedIn = false;

    this._addButtonDefaultText = 'Selecionar brinde';

  }

  /* ---------- lifecycle ---------- */
  connectedCallback() {
    this.$modal   = this.querySelector('#fgc-modal');
    this.$items   = this.querySelector('#fgc-items');
    this.$confirm = this.querySelector('#fgc-confirm');

    const addButton = this.querySelector('[data-fgc-add-selected]');
    if (addButton) {
      this._addButtonDefaultText = (addButton.textContent || '').trim() || this._addButtonDefaultText;
    }

    this._readLoginGateConfig();

    // listeners
    document.addEventListener('fg-gifts:show-slider', this._onShowSlider);
    // compat: alguns apps usam outro nome
    document.addEventListener('bogos:show-slider', this._onShowSlider);
    // Propagar elegibilidade do BOGOs para o banner
    document.addEventListener('fg-gifts:show-slider', (ev) => {
      try { document.dispatchEvent(new CustomEvent('fgc:eligibility-changed', { detail: { eligible: true, source: 'bogos' } })); } catch(_) {}
    });
    document.addEventListener('keydown', this._onEsc);
    document.addEventListener('change', this._onVariantChange, true);
    document.addEventListener('click', this._onClick, true);

    // inicializa controle do banner (div comum)
    this._initBannerBridge();

    // define custom elements necessários
    if (!customElements.get('fgc-glide')) customElements.define('fgc-glide', FgcGlide);
  }
  disconnectedCallback() {
    document.removeEventListener('fg-gifts:show-slider', this._onShowSlider);
    document.removeEventListener('bogos:show-slider', this._onShowSlider);
    document.removeEventListener('keydown', this._onEsc);
    document.removeEventListener('change', this._onVariantChange, true);
    document.removeEventListener('click', this._onClick, true);

    // cleanup do banner bridge
    try {
      if (this.$banner) this.$banner.removeEventListener('click', this._onBannerClick);
      if (this._cartEvents) {
        this._cartEvents.forEach((ev) => {
          document.removeEventListener(ev, this._bannerOnCartUpdated);
          window.removeEventListener(ev, this._bannerOnCartUpdated);
        });
      }
      document.removeEventListener('fgc:eligibility-changed', this._bannerOnEligibilityChanged);
      try { if (this._bannerMO) { this._bannerMO.disconnect(); this._bannerMO = null; } } catch(_) {}
    } catch(_) {}
  }
  /* ---------- handlers (bind como arrow p/ manter this) ---------- */
  _onShowSlider = (e) => {
    // Se a oferta mudou, limpamos bloqueios de reabertura
    const payload  = (e && e.detail && (e.detail.payload || e.detail)) || {};
    const offerKey = this._buildOfferKey(payload);
    const lastKey  = FGC_STORAGE.get(FGC_KEYS.LAST_OFFER);
    if (offerKey && offerKey !== lastKey) {
      FGC_STORAGE.del(FGC_KEYS.OPTED_OUT);
      FGC_STORAGE.del(FGC_KEYS.CHOSEN);
      FGC_STORAGE.set(FGC_KEYS.LAST_OFFER, offerKey);
    }

    // Respeitar bloqueios de reabertura automática
    const optedOut = FGC_STORAGE.get(FGC_KEYS.OPTED_OUT) === '1';
    const chosen   = FGC_STORAGE.get(FGC_KEYS.CHOSEN) === '1';

    // modo “sonda”: renderiza para atualizar HAS_OFFERS etc, mas não abre modal
    if (this._suppressAutoOpenOnce) {
      this._suppressAutoOpenOnce = false;
      this._render(payload);
      return;
    }

    if (optedOut || chosen) {
      // Quando o usuário recusou o brinde, não abrimos automaticamente,
      // mas cacheamos o payload mais recente para o banner conseguir reabrir
      // o modal depois sem depender de um novo request síncrono ao BOGOS.
      this._cachePayloadIfRenderable(payload);
      return;
    }

    this._render(payload);
    this._openModal();
  };
  async _probeOffersOnce() {
    if (this._probingOffers) return;
    this._probingOffers = true;
    try {
      await this._probeOffers();
    } finally {
      this._probingOffers = false;
    }
  }

  async _probeOffers() {
    // Evita abrir o modal quando o app responder com fg-gifts:show-slider
    this._suppressAutoOpenOnce = true;

    // Pede para o app de gifts nos mandar o slider, se houver
    try { document.dispatchEvent(new CustomEvent('fg-gifts:request-slider')); } catch(_) {}
    try { document.dispatchEvent(new CustomEvent('bogos:request-slider')); } catch(_) {}

    await new Promise((resolve) => {
      let resolved = false;
      const finish = (hasOffers) => {
        if (hasOffers === false) {
          try { FGC_STORAGE.set(FGC_KEYS.HAS_OFFERS, '0'); } catch(_) {}
        }
        if (!resolved) { resolved = true; resolve(); }
      };
      const timer = setTimeout(() => {
        document.removeEventListener('fg-gifts:show-slider', once);
        finish(false);
      }, 3000);
      const once  = () => {
        clearTimeout(timer);
        document.removeEventListener('fg-gifts:show-slider', once);
        // _onShowSlider será chamado e fará o _render sem abrir modal
        finish(true);
      };
      document.addEventListener('fg-gifts:show-slider', once, { once: true });
    });
  }

  _onEsc = (e) => {
    if (e.key === 'Escape' && this._isOpen()) this._openConfirm(); // declínio só no confirm
  };

  _onVariantChange = (e) => {
    // troca de imagem ao selecionar variante (somente dentro do componente)
    const select = e.target.closest && e.target.closest('.fgc-select');
    if (!select || !this.contains(select)) return;

    const card  = select.closest('[data-fgc-card]');
    const img   = card && card.querySelector('.fgc-thumb');
    const opt   = select.selectedOptions && select.selectedOptions[0];
    const thumb = opt && opt.dataset.thumb;
    if (img && thumb) img.src = thumb;

    // se o card já estiver selecionado, atualiza variantId
    if (card && card.getAttribute('aria-checked') === 'true') {
      this.selectedVariant = {
        id: Number(opt?.value || 0),
        offerId: opt?.dataset.offer || '',
        cardEl: card
      };
      this._toggleActionButton();
    }
  };

  _onClick = (e) => {
    if (!this.contains(e.target)) return;

    // clicar no backdrop do modal principal -> abrir confirmação
    if (e.target === this.$modal) {
      e.preventDefault(); this._openConfirm(); return;
    }

    // botão "Não quero brinde" do modal principal APENAS abre a confirmação
    if (e.target.closest('[data-fgc-decline]')) {
      e.preventDefault(); this._openConfirm(); return;
    }

    // confirmação definitiva de declínio
    if (e.target.closest('[data-fgc-confirm-decline]')) {
      this._trackOptOut('confirm_decline');
      FGC_STORAGE.set(FGC_KEYS.OPTED_OUT, '1');
      FGC_STORAGE.del(FGC_KEYS.CHOSEN);
      // Dispara evento para o banner aparecer imediatamente (continua elegível, sem brinde)
      try {
        document.dispatchEvent(new CustomEvent('fgc:eligibility-changed', {
          detail: { eligible: true, hasGift: false, source: 'optout' }
        }));
      } catch(_) {}
      this._closeConfirm();
      this._closeModal();
      return;
    }

    // manter escolhendo no confirm
    if (e.target.closest('[data-fgc-confirm-keep]')) {
      this._closeConfirm(); this._focusFirst(); return;
    }

    // selecionar um card (single-choice como "radio")
    const card = e.target.closest('[data-fgc-card]');
    if (card) {
      if (card.classList.contains('fgc-card--locked') || card.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        return;
      }

      this._selectCard(card);
      return;
    }

    // botão "Adicionar brinde" da barra inferior (usa variant selecionada)
    if (e.target.closest('[data-fgc-add-selected]')) {
      this._addSelectedGift(e.target.closest('[data-fgc-add-selected]'));
      return;
    }
  };

  /* ---------- bridge: controlar o banner direto pelo modal ---------- */
  _initBannerBridge() {
    const el = document.getElementById('fgc-cart-banner');
    this._bindBannerEl(el);

    // Observa o DOM por trocas do banner (cart drawer costuma re-renderizar)
    if (this._bannerMO) { try { this._bannerMO.disconnect(); } catch(_) {} }
    try {
      this._bannerMO = new MutationObserver(() => {
        const current = document.getElementById('fgc-cart-banner');
        if (current && current !== this.$banner) this._bindBannerEl(current);
      });
      this._bannerMO.observe(document.body, { childList: true, subtree: true });
    } catch(_) {}

    // estado inicial
    this._bannerOnCartUpdated();
  }

  _bindBannerEl(el) {
    if (el === this.$banner) return;

    // cleanup do anterior
    try { if (this.$banner) this.$banner.removeEventListener('click', this._onBannerClick); } catch(_) {}

    // define novo
    this.$banner = el || null;
    try { if (this.$banner && !this.$banner.style.cursor) this.$banner.style.cursor = 'pointer'; } catch(_) {}
    if (!this.$banner) return;

    // respeita estado inicial: se vier com atributo hidden, mantemos até recalcular
    if (this.$banner.hasAttribute('hidden')) this.$banner.hidden = true;

    // clique abre o modal
    this.$banner.addEventListener('click', this._onBannerClick);

    // reagir a mudanças de elegibilidade e eventos de carrinho
    document.addEventListener('fgc:eligibility-changed', this._bannerOnEligibilityChanged, { passive: true });
    this._cartEvents.forEach((ev) => {
      document.addEventListener(ev, this._bannerOnCartUpdated, { passive: true });
      window.addEventListener(ev, this._bannerOnCartUpdated, { passive: true });
    });
  }

  _onBannerClick = (e) => {
    try { e?.preventDefault?.(); } catch(_) {}
    this.openGiftsPopup(true);
  }

  _bannerOnEligibilityChanged = (e) => {
    try {
      if (!this.$banner || !e || !e.detail) return;
      if (typeof e.detail.eligible === 'boolean') {
        this.$banner.dataset.bogosEligible = String(e.detail.eligible);
        this._bannerUpdateVisibility();
      }
    } catch(_) {}
  }

  _bannerOnCartUpdated = async (e) => {
    if (!this.$banner) return;
    let cart = null;
    try {
      cart = (e && (e.detail?.cart || e.detail)) || null;
      if (!cart && e && e.detail && e.detail.response && e.detail.response.cart) {
        cart = e.detail.response.cart;
      }
      if (!cart || typeof cart !== 'object' || !Array.isArray(cart.items)) {
        const resp = await fetch('/cart.js', { credentials: 'same-origin' });
        cart = await resp.json();
      }
    } catch(_) { cart = { items: [], item_count: 0 }; }

    try {
      const items = cart.items || [];
      const giftStillThere = items.some((it) => it.properties && it.properties._is_gift === 'true');
      this.$banner.dataset.cartHasGift = String(giftStillThere);
      // Se o brinde foi removido manualmente, limpamos CHOSEN para permitir reabertura
      // e restauramos o CTA do modal, caso ele esteja aberto/cacheado com estado de sucesso anterior.
      if (!giftStillThere) {
        const chosenWasSet = FGC_STORAGE.get(FGC_KEYS.CHOSEN) === '1';
        try { FGC_STORAGE.del(FGC_KEYS.CHOSEN); } catch(_) {}
        if (chosenWasSet) this.selectedVariant = null;
        this._resetActionButton();
      }

      let eligible = null;
      const minQtyAttr = this.$banner.getAttribute('data-eligibility-min-qty');
      const minQty = minQtyAttr ? parseInt(minQtyAttr, 10) : null;
      const productIdAttr = this.$banner.getAttribute('data-eligibility-product-id');
      const targetProductId = productIdAttr ? String(productIdAttr) : null;

      if (Number.isInteger(minQty)) {
        if (targetProductId) {
          const qtyOfProduct = items
            .filter(it => String(it.product_id || it.productId || it.product?.id) === targetProductId)
            .reduce((acc, it) => acc + (Number(it.quantity) || 0), 0);
          eligible = qtyOfProduct >= minQty;
        } else {
          const totalQty = Number(cart.item_count || items.reduce((acc, it) => acc + (Number(it.quantity) || 0), 0));
          eligible = totalQty >= minQty;
        }
      }
      if (eligible === null) {
        if (!this.$banner.hasAttribute('data-bogos-eligible')) {
          eligible = Number(cart.item_count || 0) >= 4; // fallback padrão
        }
      }
      if (eligible !== null) {
        this.$banner.dataset.bogosEligible = String(!!eligible);
        try { FGC_STORAGE.set(FGC_KEYS.LAST_ELIGIBLE, eligible ? '1' : '0'); } catch(_) {}
        // Sinaliza mudança de elegibilidade/estado de brinde (útil p/ outros widgets)
        try {
          document.dispatchEvent(new CustomEvent('fgc:eligibility-changed', { detail: { eligible: !!eligible, hasGift: !!giftStillThere, source: 'cart-update' } }));
        } catch(_) {}
      }
    } catch(_) {}

    this._bannerUpdateVisibility();

    // Não pedir o slider do BOGOS em updates do carrinho.
    // Esse request silencioso pode fazer o app de gifts mexer no drawer durante o mesmo ciclo
    // em que componentes como "Sugestões pra você" estão se atualizando.
    // A abertura automática continua vindo do evento nativo fg-gifts:show-slider/bogos:show-slider.

  }

  _bannerUpdateVisibility() {
    if (!this.$banner) return;
    const isEligible = this.$banner.dataset.bogosEligible === 'true';
    const hasGift = this.$banner.dataset.cartHasGift === 'true';
    const optedOut = FGC_STORAGE.get(FGC_KEYS.OPTED_OUT) === '1';

    const requireDeclineAttr = this.$banner.getAttribute('data-require-decline');
    const requireDecline = (requireDeclineAttr == null) ? true : !(requireDeclineAttr === 'false' || requireDeclineAttr === '0');

    const shouldShow = isEligible && !hasGift && (optedOut || !requireDecline);
    const hide = !shouldShow;
    this.$banner.hidden = hide;
    if (hide) {
      if (!this.$banner.hasAttribute('hidden')) this.$banner.setAttribute('hidden', '');
    } else {
      if (this.$banner.hasAttribute('hidden')) this.$banner.removeAttribute('hidden');
    }
  }

  /* ---------- render ---------- */
  _render(detail) {
    const pairs = this._extractGifts(detail);

    // aplica a classe dinâmica baseada na quantidade, mesmo quando for 0
    this._applyItemsCount(pairs.length);

    if (!pairs.length) { console.warn('[FG] Nenhum gift no payload.'); return; }

    // mantém o último payload conhecido inclusive no modo bloqueado,
    // para reabrir via CTA/banner sem depender de novo evento do BOGOS.
    this.payload = detail;

    if (this._shouldLockForLogin()) {
      this._renderLoginLocked(detail, pairs);
      return;
    }

    const actionsBar = this.querySelector('.fgc-actions-bar');
    if (actionsBar) actionsBar.hidden = false;

    const declineButton = this.querySelector('[data-fgc-decline]');
    if (declineButton) declineButton.hidden = false;

    // desenha cards
    this.$items.innerHTML = pairs.map(({ gift, offerId }) => this._renderCard(gift, offerId)).join('');

    // aplica novamente após o update do DOM (idempotente)
    this._applyItemsCount(pairs.length);

    this.selectedVariant = null;
    this._resetActionButton();
  }

  _renderCard(gift, offerId) {
    const visibleVariants = (gift.variants || []).filter(v => !this._shouldHideVariantOption(gift, v));
    const variantsForSelect = visibleVariants.length ? visibleVariants : (gift.variants || []);
    const cardPayload = variantsForSelect[0] || (gift.variants && gift.variants[0]) || { id: null, title: 'Padrão', price: 0, original_price: 0, thumbnail: gift.thumbnail || '' };
    const priceHtml = Number(cardPayload.original_price) > 0
      ? `<div class="fgc-price"><del>${this._money(cardPayload.original_price)}</del><span>${this._money(cardPayload.price)}</span></div>`
      : `<div class="fgc-price">${this._money(cardPayload.price)}</div>`;

    const options = variantsForSelect.map(v => {
      const thumb = v.thumbnail || gift.thumbnail || '';
      return `<option value="${v.id || ''}" data-offer="${offerId || gift.belongs_to_offer || ''}" data-thumb="${thumb}">${v.title || 'Padrão'}</option>`;
    }).join('');

    const img = cardPayload.thumbnail || gift.thumbnail
      ? `<img class="fgc-thumb" src="${cardPayload.thumbnail || gift.thumbnail}" alt="${gift.title}" onerror="this.style.display='none'">`
      : `<div class="fgc-thumb fgc-thumb--placeholder" aria-hidden="true"></div>`;

    return `
      <li class="glide__slide">
        <div class="fgc-card" role="radio" aria-checked="false" tabindex="0" data-fgc-card data-offer="${offerId || gift.belongs_to_offer || ''}">
          ${img}
          <div class="fgc-card-body">
            <div class="fgc-title-sm">${gift.title}</div>
            ${priceHtml}
            ${options ? `<div class="fgc-variants"><select class="fgc-select">${options}</select></div>` : ''}
          </div>
        </div>
      </li>
    `;
  }

  _shouldHideVariantOption(_gift, variant) {
    try {
      const variantId = String(variant?.variant_shopify_id || variant?.id || variant?.variantId || '').split('/').pop().trim();
      return !!(
        variantId &&
        window.FGC_HIDE_VARIANT_IDS &&
        window.FGC_HIDE_VARIANT_IDS[variantId] === true
      );
    } catch (_) {
      return false;
    }
  }



  /* ---------- abrir/fechar ---------- */
  _openModal() {
    this.lastFocused = document.activeElement;
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${window.scrollY}px`;
    this.$modal.setAttribute('aria-hidden', 'false');
    this._focusFirst();
  }
  _closeModal() {
    const top = Math.abs(parseInt(document.body.style.top || '0', 10)) || 0;
    document.body.style.position = ''; document.body.style.width = ''; document.body.style.top = '';
    window.scrollTo(0, top);
    this.$modal.setAttribute('aria-hidden', 'true');
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();
  }
  _openConfirm()  { this.$confirm.setAttribute('aria-hidden', 'false'); this._focusConfirm(); }
  _closeConfirm() { this.$confirm.setAttribute('aria-hidden', 'true'); }
  _isOpen()       { return this.$modal.getAttribute('aria-hidden') === 'false'; }

  _focusFirst()   { (this.querySelector('#fgc-title') || this.$modal).focus(); }
  _focusConfirm() { (this.$confirm.querySelector('[data-fgc-confirm-decline]') || this.$confirm).focus(); }

  /* ---------- seleção e adicionar ao carrinho ---------- */
  _selectCard(cardEl) {
    if (cardEl.classList.contains('fgc-card--locked') || cardEl.getAttribute('aria-disabled') === 'true') return;
    if (this._shouldLockForLogin()) return;
    // desmarca outros
    this.querySelectorAll('.fgc-card[aria-checked="true"]').forEach(el => el.setAttribute('aria-checked', 'false'));
    cardEl.setAttribute('aria-checked', 'true');

    // lê a variante atual do select (se existir)
    const sel = cardEl.querySelector('.fgc-select');
    const opt = sel ? sel.selectedOptions[0] : null;

    this.selectedVariant = {
      id: Number(opt?.value || 0),
      offerId: opt?.dataset.offer || cardEl.getAttribute('data-offer') || '',
      cardEl
    };
    this._toggleActionButton();
  }

  _toggleActionButton() {
    const btn = this.querySelector('[data-fgc-add-selected]');
    if (!btn) return;
    btn.disabled = !(this.selectedVariant && this.selectedVariant.id);
  }

  _resetActionButton() {
    const btn = this.querySelector('[data-fgc-add-selected]');
    if (!btn) return;
    btn.textContent = this._addButtonDefaultText || 'Selecionar brinde';
    this._toggleActionButton();
  }

  _addSelectedGift(btn) {
    if (this._shouldLockForLogin()) return;
    if (!this.payload || typeof this.payload.addGiftToCartFunc !== 'function') return;
    if (!this.selectedVariant || !this.selectedVariant.id) return;

    const oldText = btn.textContent;
    btn.disabled = true; btn.textContent = 'Adicionando...';

    const add = this.selectedVariant.offerId
      ? this.payload.addGiftToCartFunc(this.selectedVariant.id, 1, this.selectedVariant.offerId)
      : this.payload.addGiftToCartFunc(this.selectedVariant.id, 1);

    Promise.resolve(add)
      .then(async () => {
        // marca opt-in e impede reabertura automática
        FGC_STORAGE.set(FGC_KEYS.CHOSEN, '1');
        FGC_STORAGE.del(FGC_KEYS.OPTED_OUT);

        // Sinalizar inelegibilidade imediata ao banner (pois há brinde no carrinho)
        try { document.dispatchEvent(new CustomEvent('fgc:eligibility-changed', { detail: { eligible: false, hasGift: true, source: 'gift-added' } })); } catch(_) {}

        btn.textContent = 'Adicionado ✓';

        // O BOGOS adiciona o brinde no backend, mas nem sempre atualiza visualmente
        // o cart drawer que já está aberto. Forçamos o refresh do front após o add.
        try {
          await this._refreshCartDrawer();
        } catch (_) {}

        setTimeout(() => this._closeModal(), 500);
      })
      .catch(err => {
        console.error('[FG] erro ao adicionar brinde:', err);
        btn.textContent = 'Tentar novamente';
      })
      .finally(() => {
        btn.disabled = false;
        setTimeout(() => {
          if (this._isOpen() && FGC_STORAGE.get(FGC_KEYS.CHOSEN) !== '1') {
            btn.textContent = oldText;
          }
        }, 1500);
      });
  }

  /* ---------- abrir drawer & atualizar (mimic add_to_cart) ---------- */
  async _openCartDrawer() {
    try { document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true })); } catch(_) {}
    try { window.dispatchEvent(new CustomEvent('cart:open')); } catch(_) {}

    // APIs comuns do tema
    try { if (window.CartDrawer && typeof window.CartDrawer.open === 'function') { window.CartDrawer.open(); return; } } catch(_) {}
    try { if (window.theme && window.theme.cart && typeof window.theme.cart.open === 'function') { window.theme.cart.open(); return; } } catch(_) {}

    // Gatilhos usuais no markup
    const triggers = document.querySelectorAll('[data-open-cart], [data-cart-open], [data-action="open-cart"], [href="#cart"]');
    for (const t of triggers) { try { if (typeof t.click === 'function') { t.click(); return; } } catch(_) {} }

    // Fallback simples
    const drawer = document.querySelector('[data-cart-drawer], #CartDrawer, cart-drawer, .cart-drawer');
    if (drawer) {
      try { drawer.classList.add('is-open','active','open','animate'); } catch(_) {}
      try { document.body.classList.add('overflow-hidden','cart-open'); } catch(_) {}
    }
  }

  async _refreshCartDrawer() {
    // coalescer múltiplas chamadas em rajada
    if (this._refreshingCart) { this._pendingCartRefresh = true; return; }
    this._refreshingCart = true; this._pendingCartRefresh = false;

    let cart = null;
    try {
      const resp = await fetch('/cart.js', { credentials: 'same-origin' });
      cart = await resp.json();
    } catch(_) { cart = null; }

    try {
      // 1) Preferir a API do drawer do tema se existir
      const drawerEl = document.querySelector('cart-drawer');
      if (drawerEl && typeof drawerEl.updateCart === 'function') {
        try { drawerEl.updateCart(); } catch(_) {}
      } else {
        // 2) Fallback: Sections Rendering API
        const drawer = document.querySelector('[data-cart-drawer], #CartDrawer, cart-drawer, .cart-drawer');
        const sectionEls = drawer ? Array.from(drawer.querySelectorAll('[data-section-id]')) : [];
        const idsSet = new Set(sectionEls.map(el => el.getAttribute('data-section-id')).filter(Boolean));
        // IDs conhecidos do tema
        ['cart-drawer','cart-icon-bubble'].forEach(id => idsSet.add(id));
        // body pode declarar seções adicionais
        const hinted = (document.body.dataset.cartSections || document.body.getAttribute('data-cart-sections') || '')
          .split(',').map(s => s.trim()).filter(Boolean);
        hinted.forEach(id => idsSet.add(id));
        const ids = Array.from(idsSet);

        if (ids.length) {
          const url = `${window.location.pathname}?sections=${encodeURIComponent(ids.join(','))}`;
          try {
            const sresp = await fetch(url, { credentials: 'same-origin' });
            const json = await sresp.json();
            ids.forEach((id) => {
              const html = json[id]; if (!html) return;
              // tenta por [data-section-id], depois por #id
              let target = document.querySelector(`[data-section-id="${CSS.escape(id)}"]`);
              if (!target) target = document.getElementById(id);
              if (!target) return;
              const rect = target.getBoundingClientRect();
              target.style.minHeight = rect.height + 'px';
              // quando o target for um wrapper de section, isola o .shopify-section se existir
              try {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const root = doc.querySelector('.shopify-section') || doc.body;
                target.innerHTML = root.innerHTML;
              } catch(_) { target.innerHTML = html; }
              target.style.minHeight = '';
              try { target.dispatchEvent(new CustomEvent('section:load', { bubbles: true })); } catch(_) {}
            });
          } catch(_) {}
        }
      }

      // 3) Atualiza contadores/subtotais globais (header/bubble), se existirem
      if (cart) {
        const count = Number.isFinite(parseInt(cart.item_count,10)) ? parseInt(cart.item_count,10) : 0;
        const n = Number(cart.total_price||0); const val = n>=1000 ? n/100 : n;
        const subtotal = (function(){ try { return val.toLocaleString('pt-BR',{style:'currency',currency:'BRL'});}catch(_){return 'R$ '+val.toFixed(2).replace('.',',');}})();

        const badgeSelectors = ['[data-cart-count]','[data-cart-items-count]','.cart-count','.header__cart-count','.js-cart-count'];
        const subtotalSelectors = ['[data-cart-subtotal]','.cart-subtotal','.header__cart-subtotal'];

        badgeSelectors.forEach(sel => document.querySelectorAll(sel).forEach(el => {
          try { el.textContent = String(count); } catch(_) {}
          try { el.setAttribute('data-count', String(count)); } catch(_) {}
        }));
        subtotalSelectors.forEach(sel => document.querySelectorAll(sel).forEach(el => {
          try { el.textContent = String(subtotal); } catch(_) {}
        }));
      }

      // 4) Eventos padrão p/ scripts do tema
      this._broadcastCartUpdate(cart);
    } finally {
      this._refreshingCart = false;
      if (this._pendingCartRefresh) { this._pendingCartRefresh = false; try { await this._refreshCartDrawer(); } catch(_) {} }
    }
  }

  /* ---------- analytics ---------- */
  _trackOptOut(context) {
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'gift_opt_out', context: context || 'confirm' }); } catch(_) {}
    try { window.ShopifyAnalytics?.lib?.track('Gift Opt Out', { context: context || 'confirm' }); } catch(_) {}
    try { document.dispatchEvent(new CustomEvent('fgc:opt_out', { detail: { context: context || 'confirm' } })); } catch(_) {}
  }

  /* ---------- helpers ---------- */
  _broadcastCartUpdate(cart) {
    try {
      const detail = cart ? { cart } : {};
      const names = [
        'cart:updated','cart-updated','cart:change','cart:changed',
        'cart:refresh','cart-drawer:updated','cart-drawer:change','ajaxCart:afterUpdate'
      ];
      names.forEach((n) => {
        try { document.dispatchEvent(new CustomEvent(n, { detail, bubbles: true })); } catch(_) {}
        try { window.dispatchEvent(new CustomEvent(n, { detail })); } catch(_) {}
      });
      // tenta refreshers comuns do tema
      try { if (window.CartDrawer && typeof window.CartDrawer.refresh === 'function') { window.CartDrawer.refresh(); } } catch(_) {}
      try { if (window.theme && window.theme.cart && typeof window.theme.cart.getCart === 'function') { window.theme.cart.getCart(); } } catch(_) {}
      try { if (typeof window.refreshCart === 'function') { window.refreshCart(); } } catch(_) {}
    } catch(_) {}
  }

  _money(v) {
    const n = Number(v || 0);
    const val = n >= 1000 ? n / 100 : n;
    try { return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); }
    catch(_) { return 'R$ ' + val.toFixed(2).replace('.', ','); }
  }

  _cachePayloadIfRenderable(detail) {
    try {
      const pairs = this._extractGifts(detail);
      this._applyItemsCount(pairs.length);
      if (!pairs.length) return false;
      this.payload = detail;
      return true;
    } catch(_) {
      return false;
    }
  }

  _extractGifts(detail) {
    // coleta todos os gifts presentes em estruturas comuns do app
    const list = [];
    const seen = new WeakSet();
    const pushGift = (gift, offerId) => {
      if (!gift) return;
      const variants = Array.isArray(gift.variants) ? gift.variants : [];
      const normVariants = variants.map(v => ({
        id: Number(v.variant_shopify_id || v.id || v.variantId || 0),
        variant_shopify_id: v.variant_shopify_id || v.id || v.variantId || '',
        variantId: v.variantId || v.variant_shopify_id || v.id || '',
        title: v.variant_title || v.title || v.name || 'Padrão',
        price: Number(v.price || v.discounted_price || 0),
        original_price: Number(v.original_price || v.compare_at_price || 0),
        thumbnail: (v.thumbnail || v.image || (v.featured_image && (v.featured_image.url || v.featured_image.src))) || ''
      })).filter(v => v.id);

      list.push({
        gift: {
          title: gift.title || gift.product_title || 'Gift',
          thumbnail: gift.thumbnail || gift.image || (normVariants[0] && normVariants[0].thumbnail) || '',
          variants: normVariants.length ? normVariants : [{ id: 0, title: 'Padrão', price: 0, original_price: 0, thumbnail: '' }],
          belongs_to_offer: gift.belongs_to_offer || offerId || ''
        },
        offerId: gift.belongs_to_offer || offerId || ''
      });
    };

    const walk = (node, offerId = '') => {
      if (!node || typeof node !== 'object') return;
      if (seen.has(node)) return; // prevent cycles
      seen.add(node);
      if (Array.isArray(node)) { node.forEach(n => walk(n, offerId)); return; }
      const maybeOffer = node.offer_id || node.offerId || node.id || offerId;

      if (Array.isArray(node.gifts)) node.gifts.forEach(g => pushGift(g, maybeOffer));
      if (Array.isArray(node.giftsOffer)) node.giftsOffer.forEach(g => {
        if (g && Array.isArray(g.gifts)) g.gifts.forEach(x => pushGift(x, g.id || maybeOffer));
        else pushGift(g, maybeOffer);
      });

      // campos genéricos / variações comuns em apps de gifts
      [
        'items','data','list','children','offers','products',
        'available_gifts','availableGifts','eligible_gifts','eligibleGifts',
        'gifts_list','gift_list','giftList','rewards','choices'
      ].forEach(k => node[k] && walk(node[k], maybeOffer));
    };

    walk(detail);
    return list;
  }

  _readLoginGateConfig() {
    this.requiresLogin = this.dataset.fgcRequiresLogin === 'true';
    this.customerLoggedIn = this.dataset.fgcCustomerLoggedIn === 'true';
  }

  _shouldLockForLogin() {
    return this.requiresLogin === true && this.customerLoggedIn === false;
  }

  _renderLoginLocked(detail, pairs) {
    const safePairs = Array.isArray(pairs) ? pairs : this._extractGifts(detail);
    this._applyItemsCount(safePairs.length);
    if (!safePairs.length) { console.warn('[FG] Nenhum gift no payload.'); return; }

    this.payload = detail;
    this.selectedVariant = null;

    const template = this.querySelector('[data-fgc-login-locked-template]');
    if (!template || !template.content) {
      console.warn('[FG] Template de login bloqueado não encontrado.');
      return;
    }

    const fragment = document.createDocumentFragment();

    safePairs.forEach(({ gift }) => {
      const clone = template.content.cloneNode(true);
      const title = gift.title || 'Brinde desbloqueado';
      const thumb = gift.thumbnail || '';
      const titleEl = clone.querySelector('[data-fgc-locked-title]');
      const imageSlot = clone.querySelector('[data-fgc-locked-image]');

      if (titleEl) titleEl.textContent = title;

      if (imageSlot) {
        imageSlot.textContent = '';

        if (thumb) {
          const img = document.createElement('img');
          img.className = 'fgc-thumb fgc-thumb--blurred';
          img.src = thumb;
          img.alt = title;
          img.addEventListener('error', () => { img.style.display = 'none'; }, { once: true });
          imageSlot.appendChild(img);
        } else {
          const placeholder = document.createElement('div');
          placeholder.className = 'fgc-thumb fgc-thumb--placeholder fgc-thumb--blurred';
          placeholder.setAttribute('aria-hidden', 'true');
          imageSlot.appendChild(placeholder);
        }
      }

      fragment.appendChild(clone);
    });

    this.$items.innerHTML = '';
    this.$items.appendChild(fragment);
    this._applyItemsCount(safePairs.length);

    const actionsBar = this.querySelector('.fgc-actions-bar');
    if (actionsBar) actionsBar.hidden = false;

    const addButton = this.querySelector('[data-fgc-add-selected]');
    if (addButton) addButton.disabled = true;
  }

  _buildOfferKey(detail) {
    // cria uma assinatura simples da oferta atual (para habilitar reabertura quando mudar)
    try {
      const pairs = this._extractGifts(detail);
      if (!pairs.length) return '';
      const ids = pairs.map(p => (p.offerId || '') + '|' + (p.gift && p.gift.title || '')).join(',');
      return String(ids).slice(0, 512);
    } catch(_) { return ''; }
  }

  /* ---------- API pública ---------- */
  async openGiftsPopup(force) {
    // Bloqueio: se já escolheu um brinde, não reabrimos
    const chosen = FGC_STORAGE.get(FGC_KEYS.CHOSEN) === '1';
    if (chosen) return;

    // Se já temos payload, renderiza e abre (call explícito ignora OPTED_OUT)
    if (this.payload) { this._render(this.payload); this._openModal(); return; }

    // Call explícito do banner nunca deve ser consumido por uma sonda silenciosa anterior.
    this._suppressAutoOpenOnce = false;

    // 1) Sinaliza para o app que queremos o slider (nomes comuns)
    try { document.dispatchEvent(new CustomEvent('fg-gifts:request-slider')); } catch(_) {}
    try { document.dispatchEvent(new CustomEvent('bogos:request-slider')); } catch(_) {}

    // 2) Se existir alguma API global do app, tenta chamá-la (sem quebrar se não existir)
    try { if (typeof window.openGiftsPopup === 'function') { window.openGiftsPopup(true); } } catch(_) {}
    try { if (window.BOGOS && typeof window.BOGOS.open === 'function') { window.BOGOS.open(); } } catch(_) {}

    // 3) Aguarda o evento oficial do app por alguns segundos
    await new Promise((resolve) => {
      let resolved = false;
      const finish = () => { if (!resolved) { resolved = true; resolve(); } };
      const cleanup = () => {
        document.removeEventListener('fg-gifts:show-slider', once);
        document.removeEventListener('bogos:show-slider', once);
      };
      const timer = setTimeout(() => { cleanup(); finish(); }, 4000);
      const once  = (ev) => {
        clearTimeout(timer);
        cleanup();
        const payload = (ev && ev.detail && (ev.detail.payload || ev.detail)) || {};
        this._render(payload);
        this._openModal();
        finish();
      };
      document.addEventListener('fg-gifts:show-slider', once, { once: true });
      document.addEventListener('bogos:show-slider', once, { once: true });
    });
  }

  // Atualiza as classes dinâmicas de contagem de gifts no modal (e remove antigas/legadas)
  _applyItemsCount(count) {
    try {
      const c = Math.max(0, Number(count || 0));
      if (!this.$modal) return;

      // atributo data para seletores
      this.$modal.setAttribute('data-fgc-count', String(c));

      // remove classes anteriores dinâmicas: fgc-count-* e fgc-has-*
      const toRemove = [];
      this.$modal.classList.forEach(cls => {
        if (cls.startsWith('fgc-count-') || cls.startsWith('fgc-has-')) toRemove.push(cls);
      });
      if (toRemove.length) this.$modal.classList.remove(...toRemove);

      // adiciona somente a classe com o valor atual
      this.$modal.classList.add(`fgc-count-${c}`);
    } catch(_) {}
  }
}

/* registra o web component do modal (HTML já existe no snippet) */
if (!customElements.get('freegifts-modal')) {
  customElements.define('freegifts-modal', FreeGiftsModal);
}